import { AxiosResponse } from 'axios';
import { isValidWorkyServerResponseWithData } from '../../../common/WorkyAPIUtils';
import {
  RemoteExecutionServiceImpl,
  RemoteGETCallInputObject,
  RemotePOSTCallInputObject,
  ServerResponse,
} from '../../../remoteExecution/RemoteExecutionServiceImpl';
import type {
  ApplyAttributeGroupToWorkerReqDTO,
  AgencyAttributeDTO,
  AgencyAttributeListRespDTO,
  ArchiveAgencyAttributeReqDTO,
  ArchiveAttributeGroupReqDTO,
  AttributeGroupDTO,
  AttributeGroupListRespDTO,
  CreateAgencyAttributeReqDTO,
  CreateAttributeGroupReqDTO,
  OrgAttributeRequirementDTO,
  OrgAttributeRequirementListRespDTO,
  RemoveOrgAttributeRequirementReqDTO,
  SearchWorkersByAttributesReqDTO,
  RenameAgencyAttributeReqDTO,
  RenameAgencyAttributeSelectOptionReqDTO,
  DisableAgencyAttributeSelectOptionReqDTO,
  AddAgencyAttributeSelectOptionReqDTO,
  RenameAttributeGroupReqDTO,
  UpsertOrgAttributeRequirementReqDTO,
  UpdateAttributeGroupMembersReqDTO,
  UpsertWorkerAttributeAssignmentReqDTO,
  WorkerAttributeAssignmentDTO,
  WorkerAttributeAssignmentListRespDTO,
  WorkerAttributeSearchListRespDTO,
  WorkerAttributeSearchResultDTO,
  RemoveWorkerAttributeAssignmentReqDTO,
} from '../interfaces/AgencyAttributesInterfaces';

const backendApiBasePathLocal: string = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL ?? '';
const remoteCallObject = RemoteExecutionServiceImpl();

const ATTRIBUTES_BASE_PATH: string = 'v1/agency/attributes/';
const ATTRIBUTES_RENAME_PATH: string = 'v1/agency/attributes/rename/';
const ATTRIBUTES_ARCHIVE_PATH: string = 'v1/agency/attributes/archive/';
const ATTRIBUTES_OPTIONS_ADD_PATH: string = 'v1/agency/attributes/options/add/';
const ATTRIBUTES_OPTIONS_RENAME_PATH: string = 'v1/agency/attributes/options/rename/';
const ATTRIBUTES_OPTIONS_DISABLE_PATH: string = 'v1/agency/attributes/options/disable/';
const GROUPS_BASE_PATH: string = 'v1/agency/attributes/groups/';
const GROUPS_RENAME_PATH: string = 'v1/agency/attributes/groups/rename/';
const GROUPS_ARCHIVE_PATH: string = 'v1/agency/attributes/groups/archive/';
const GROUPS_MEMBERS_PATH: string = 'v1/agency/attributes/groups/members/';
const WORKER_ASSIGNMENTS_BASE_PATH: string = 'v1/agency/attributes/workers/assignments/';
const WORKER_ASSIGNMENTS_REMOVE_PATH: string = 'v1/agency/attributes/workers/assignments/remove/';
const WORKER_ASSIGNMENTS_APPLY_GROUP_PATH: string =
  'v1/agency/attributes/workers/assignments/apply-group/';
const WORKER_ATTRIBUTE_SEARCH_PATH: string = 'v1/agency/attributes/workers/search/';
const ORG_REQUIREMENTS_BASE_PATH: string = 'v1/agency/attributes/org-requirements/';
const ORG_REQUIREMENTS_REMOVE_PATH: string = 'v1/agency/attributes/org-requirements/remove/';

const ListAgencyAttributesAPICall = async (
  includeArchived: boolean,
): Promise<readonly AgencyAttributeDTO[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_BASE_PATH}`,
    headers: {},
    queryParams: { includeArchived: String(includeArchived) },
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeListRespDTO, Error>> =
      await remoteCallObject.remoteGETCallV2<ServerResponse<AgencyAttributeListRespDTO, Error>>(
        getCallInput,
      );
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data.attributes ?? [];
    }
    return null;
  } catch (error) {
    console.error('ListAgencyAttributesAPICall failed', error);
    return null;
  }
};

const CreateAgencyAttributeAPICall = async (
  request: CreateAgencyAttributeReqDTO,
): Promise<AgencyAttributeDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_BASE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AgencyAttributeDTO, Error>,
        CreateAgencyAttributeReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('CreateAgencyAttributeAPICall failed', error);
    return null;
  }
};

const RenameAgencyAttributeAPICall = async (
  request: RenameAgencyAttributeReqDTO,
): Promise<AgencyAttributeDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_RENAME_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AgencyAttributeDTO, Error>,
        RenameAgencyAttributeReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('RenameAgencyAttributeAPICall failed', error);
    return null;
  }
};

const ArchiveAgencyAttributeAPICall = async (
  request: ArchiveAgencyAttributeReqDTO,
): Promise<AgencyAttributeDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_ARCHIVE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AgencyAttributeDTO, Error>,
        ArchiveAgencyAttributeReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('ArchiveAgencyAttributeAPICall failed', error);
    return null;
  }
};

const AddAgencyAttributeSelectOptionAPICall = async (
  request: AddAgencyAttributeSelectOptionReqDTO,
): Promise<AgencyAttributeDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_OPTIONS_ADD_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AgencyAttributeDTO, Error>,
        AddAgencyAttributeSelectOptionReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('AddAgencyAttributeSelectOptionAPICall failed', error);
    return null;
  }
};

const RenameAgencyAttributeSelectOptionAPICall = async (
  request: RenameAgencyAttributeSelectOptionReqDTO,
): Promise<AgencyAttributeDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_OPTIONS_RENAME_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AgencyAttributeDTO, Error>,
        RenameAgencyAttributeSelectOptionReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('RenameAgencyAttributeSelectOptionAPICall failed', error);
    return null;
  }
};

const DisableAgencyAttributeSelectOptionAPICall = async (
  request: DisableAgencyAttributeSelectOptionReqDTO,
): Promise<AgencyAttributeDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ATTRIBUTES_OPTIONS_DISABLE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AgencyAttributeDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AgencyAttributeDTO, Error>,
        DisableAgencyAttributeSelectOptionReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('DisableAgencyAttributeSelectOptionAPICall failed', error);
    return null;
  }
};

const ListAttributeGroupsAPICall = async (
  includeArchived: boolean,
): Promise<readonly AttributeGroupDTO[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${GROUPS_BASE_PATH}`,
    headers: {},
    queryParams: { includeArchived: String(includeArchived) },
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AttributeGroupListRespDTO, Error>> =
      await remoteCallObject.remoteGETCallV2<ServerResponse<AttributeGroupListRespDTO, Error>>(
        getCallInput,
      );
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data.groups ?? [];
    }
    return null;
  } catch (error) {
    console.error('ListAttributeGroupsAPICall failed', error);
    return null;
  }
};

const CreateAttributeGroupAPICall = async (
  request: CreateAttributeGroupReqDTO,
): Promise<AttributeGroupDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${GROUPS_BASE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AttributeGroupDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AttributeGroupDTO, Error>,
        CreateAttributeGroupReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('CreateAttributeGroupAPICall failed', error);
    return null;
  }
};

const RenameAttributeGroupAPICall = async (
  request: RenameAttributeGroupReqDTO,
): Promise<AttributeGroupDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${GROUPS_RENAME_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AttributeGroupDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AttributeGroupDTO, Error>,
        RenameAttributeGroupReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('RenameAttributeGroupAPICall failed', error);
    return null;
  }
};

const ArchiveAttributeGroupAPICall = async (
  request: ArchiveAttributeGroupReqDTO,
): Promise<AttributeGroupDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${GROUPS_ARCHIVE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AttributeGroupDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AttributeGroupDTO, Error>,
        ArchiveAttributeGroupReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('ArchiveAttributeGroupAPICall failed', error);
    return null;
  }
};

const UpdateAttributeGroupMembersAPICall = async (
  request: UpdateAttributeGroupMembersReqDTO,
): Promise<AttributeGroupDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${GROUPS_MEMBERS_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<AttributeGroupDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<AttributeGroupDTO, Error>,
        UpdateAttributeGroupMembersReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('UpdateAttributeGroupMembersAPICall failed', error);
    return null;
  }
};

const ListWorkerAttributeAssignmentsAPICall = async (
  primaryWorkyUserId: string,
): Promise<readonly WorkerAttributeAssignmentDTO[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${WORKER_ASSIGNMENTS_BASE_PATH}`,
    headers: {},
    queryParams: { primaryWorkyUserId },
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<WorkerAttributeAssignmentListRespDTO, Error>> =
      await remoteCallObject.remoteGETCallV2<
        ServerResponse<WorkerAttributeAssignmentListRespDTO, Error>
      >(getCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data.assignments ?? [];
    }
    return null;
  } catch (error) {
    console.error('ListWorkerAttributeAssignmentsAPICall failed', error);
    return null;
  }
};

const SearchWorkersByAttributesAPICall = async (
  request: SearchWorkersByAttributesReqDTO,
): Promise<readonly WorkerAttributeSearchResultDTO[] | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${WORKER_ATTRIBUTE_SEARCH_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<WorkerAttributeSearchListRespDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<WorkerAttributeSearchListRespDTO, Error>,
        SearchWorkersByAttributesReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data.workers ?? [];
    }
    return null;
  } catch (error) {
    console.error('SearchWorkersByAttributesAPICall failed', error);
    return null;
  }
};

const UpsertWorkerAttributeAssignmentAPICall = async (
  request: UpsertWorkerAttributeAssignmentReqDTO,
): Promise<WorkerAttributeAssignmentDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${WORKER_ASSIGNMENTS_BASE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<WorkerAttributeAssignmentDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<WorkerAttributeAssignmentDTO, Error>,
        UpsertWorkerAttributeAssignmentReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('UpsertWorkerAttributeAssignmentAPICall failed', error);
    return null;
  }
};

const RemoveWorkerAttributeAssignmentAPICall = async (
  request: RemoveWorkerAttributeAssignmentReqDTO,
): Promise<WorkerAttributeAssignmentDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${WORKER_ASSIGNMENTS_REMOVE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<WorkerAttributeAssignmentDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<WorkerAttributeAssignmentDTO, Error>,
        RemoveWorkerAttributeAssignmentReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('RemoveWorkerAttributeAssignmentAPICall failed', error);
    return null;
  }
};

const ApplyAttributeGroupToWorkerAPICall = async (
  request: ApplyAttributeGroupToWorkerReqDTO,
): Promise<readonly WorkerAttributeAssignmentDTO[] | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${WORKER_ASSIGNMENTS_APPLY_GROUP_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<WorkerAttributeAssignmentListRespDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<WorkerAttributeAssignmentListRespDTO, Error>,
        ApplyAttributeGroupToWorkerReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data.assignments ?? [];
    }
    return null;
  } catch (error) {
    console.error('ApplyAttributeGroupToWorkerAPICall failed', error);
    return null;
  }
};

const ListOrgAttributeRequirementsAPICall = async (
  orgName: string,
): Promise<readonly OrgAttributeRequirementDTO[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ORG_REQUIREMENTS_BASE_PATH}`,
    headers: {},
    queryParams: { orgName },
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<OrgAttributeRequirementListRespDTO, Error>> =
      await remoteCallObject.remoteGETCallV2<
        ServerResponse<OrgAttributeRequirementListRespDTO, Error>
      >(getCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data.requirements ?? [];
    }
    return null;
  } catch (error) {
    console.error('ListOrgAttributeRequirementsAPICall failed', error);
    return null;
  }
};

const UpsertOrgAttributeRequirementAPICall = async (
  request: UpsertOrgAttributeRequirementReqDTO,
): Promise<OrgAttributeRequirementDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ORG_REQUIREMENTS_BASE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<OrgAttributeRequirementDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<OrgAttributeRequirementDTO, Error>,
        UpsertOrgAttributeRequirementReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('UpsertOrgAttributeRequirementAPICall failed', error);
    return null;
  }
};

const RemoveOrgAttributeRequirementAPICall = async (
  request: RemoveOrgAttributeRequirementReqDTO,
): Promise<OrgAttributeRequirementDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}${ORG_REQUIREMENTS_REMOVE_PATH}`,
    headers: {},
    object: request,
  };
  try {
    const apiResponse: AxiosResponse<ServerResponse<OrgAttributeRequirementDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<OrgAttributeRequirementDTO, Error>,
        RemoveOrgAttributeRequirementReqDTO
      >(postCallInput);
    if (isValidWorkyServerResponseWithData(apiResponse)) {
      return apiResponse.data.data;
    }
    return null;
  } catch (error) {
    console.error('RemoveOrgAttributeRequirementAPICall failed', error);
    return null;
  }
};

export {
  ListAgencyAttributesAPICall,
  CreateAgencyAttributeAPICall,
  RenameAgencyAttributeAPICall,
  ArchiveAgencyAttributeAPICall,
  AddAgencyAttributeSelectOptionAPICall,
  RenameAgencyAttributeSelectOptionAPICall,
  DisableAgencyAttributeSelectOptionAPICall,
  ListAttributeGroupsAPICall,
  CreateAttributeGroupAPICall,
  RenameAttributeGroupAPICall,
  ArchiveAttributeGroupAPICall,
  UpdateAttributeGroupMembersAPICall,
  ListWorkerAttributeAssignmentsAPICall,
  SearchWorkersByAttributesAPICall,
  UpsertWorkerAttributeAssignmentAPICall,
  RemoveWorkerAttributeAssignmentAPICall,
  ApplyAttributeGroupToWorkerAPICall,
  ListOrgAttributeRequirementsAPICall,
  UpsertOrgAttributeRequirementAPICall,
  RemoveOrgAttributeRequirementAPICall,
};
